/**
 * Aggregation helpers shared across g-kpi, g-chart, g-card-grid.
 *
 * Metric config shape (single-field):
 *   { metric: 'sum'|'count'|'avg'|'min'|'max', field: 'fieldName', predicate? }
 *
 * Two-field:
 *   { metric: 'percent', numerator: 'Amount Paid', denominator: 'Original Balance', predicate? }
 *
 * Predicate is orthogonal to reduce — any metric accepts an optional predicate
 * that filters `records` before aggregating. Examples:
 *   { metric: 'sum', field: 'Amount', predicate: { field: 'Stage', op: { in: ['Open'] } } }
 *   { metric: 'count', predicate: { field: 'Status', op: { eq: 'Active' } } }
 *   { metric: 'avg', field: 'Score', predicate: { field: 'Role', op: { eq: 'User' } } }
 *
 * `countWhere` is kept as a back-compat alias for `count + predicate`.
 *
 * Returns a number (or NaN for invalid configs). Callers format per render hint.
 */
import { evalRecord, evalPredicate } from './predicate.js';
import { getField, refLabel } from './fields.js';
import { compareText } from './collate.js';

export function aggregate(records, config) {
  if (!Array.isArray(records) || !config || !config.metric) return NaN;
  const metric = config.metric;

  // Apply optional predicate on every metric. Filter is orthogonal to reduce —
  // `{ metric: 'sum', field, predicate }` works the same way `countWhere` did.
  const scope = config.predicate
    ? records.filter(r => evalRecord(r, config.predicate))
    : records;

  if (metric === 'count' || metric === 'countWhere') return scope.length;

  if (metric === 'percent') {
    const num = sum(scope, config.numerator);
    const den = sum(scope, config.denominator);
    if (!den) return 0;
    return num / den;
  }

  // single-field metrics
  const field = config.field;
  if (!field) return NaN;

  switch (metric) {
    case 'sum':  return sum(scope, field);
    case 'avg': {
      const vals = numericValues(scope, field);
      if (!vals.length) return 0;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    }
    case 'min': {
      const vals = numericValues(scope, field);
      return vals.length ? Math.min(...vals) : 0;
    }
    case 'max': {
      const vals = numericValues(scope, field);
      return vals.length ? Math.max(...vals) : 0;
    }
    default: return NaN;
  }
}

// Group records by a field, then apply a metric per group.
// Returns array of { key, value, count } sorted by config.sort ('value-desc' default).
//
//   groupAggregate(records, { groupBy: 'Status', metric: 'count' })
//   groupAggregate(records, { groupBy: 'Assigned To', metric: 'sum', field: 'Remaining Balance' })
export function groupAggregate(records, config) {
  if (!Array.isArray(records) || !config?.groupBy) return [];
  // Filter BEFORE grouping so { predicate } narrows the record set across every
  // group simultaneously (same semantics as aggregate()'s predicate).
  const scope = config.predicate
    ? records.filter(r => evalRecord(r, config.predicate))
    : records;
  const groupField = typeof config.groupBy === 'string' ? config.groupBy : config.groupBy.field;
  // Optional date bucketing: groupBy: { field: 'closeDate', bucket: 'month' }
  // derives a sortable YYYY-MM / YYYY-Qn / YYYY-Www / YYYY-MM-DD / YYYY key
  // from a Date value so time-series charts don't require a derived field in
  // the backend.
  const bucket = typeof config.groupBy === 'object' ? config.groupBy.bucket : null;
  const keyFor = bucket ? (v) => dateKey(v, bucket) : (v) => v;
  const groups = new Map();
  for (const r of scope) {
    // A populated ref groups by its name, not by object identity — which
    // would put every record in a group of its own.
    const raw = refLabel(getField(r, groupField));
    const key = keyFor(raw);
    if (key == null || key === '') continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  const result = [];
  for (const [key, recs] of groups.entries()) {
    const value = aggregate(recs, { metric: config.metric || 'count', field: config.field });
    result.push({ key, value, count: recs.length });
  }
  // Default sort: value descending. Spec can opt out with sort: 'none'|'key-asc'|'key-desc'|'value-asc'
  const sortMode = config.sort || 'value-desc';
  if (sortMode === 'value-desc') result.sort((a, b) => b.value - a.value);
  else if (sortMode === 'value-asc') result.sort((a, b) => a.value - b.value);
  else if (sortMode === 'key-asc') result.sort((a, b) => compareText(a.key, b.key));
  else if (sortMode === 'key-desc') result.sort((a, b) => compareText(b.key, a.key));
  return result;
}

// Derive a sortable string key from a date-like value, scoped to a bucket.
// Returns null for non-date / empty values so groupAggregate drops them
// (consistent with the string-groupBy null-skip behavior).
function dateKey(value, bucket) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const dayOfMonth = d.getUTCDate();
  const pad = (n) => String(n).padStart(2, '0');
  switch (bucket) {
    case 'day':     return `${y}-${pad(m)}-${pad(dayOfMonth)}`;
    case 'week': {
      // ISO 8601 week — week starts Monday, weeks numbered 01..53
      const copy = new Date(Date.UTC(y, m - 1, dayOfMonth));
      const dayNum = copy.getUTCDay() || 7;
      copy.setUTCDate(copy.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(copy.getUTCFullYear(), 0, 1));
      const weekNum = Math.ceil(((copy - yearStart) / 86400000 + 1) / 7);
      return `${copy.getUTCFullYear()}-W${pad(weekNum)}`;
    }
    case 'month':   return `${y}-${pad(m)}`;
    case 'quarter': return `${y}-Q${Math.ceil(m / 3)}`;
    case 'year':    return String(y);
    default:        return null;  // unknown bucket → skip (validator catches)
  }
}

function sum(records, field) {
  return numericValues(records, field).reduce((a, b) => a + b, 0);
}

function numericValues(records, field) {
  const out = [];
  for (const r of records) {
    const v = getField(r, field);
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (!isNaN(n)) out.push(n);
  }
  return out;
}

// Standalone predicate eval (re-exported convenience).
export { evalPredicate, evalRecord };
